import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Loading } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconArrowBack, IconEdit } from '@tabler/icons-react'
import { useClient } from '@/stores/useClient.ts'
import { BasketItem } from '@/models/shop.ts'
import { BookDesignEditor } from '@/components/book/BookDesignEditor.tsx'
import { CropConfirmModal, useCropConfirmModal } from '@/components/shop/CropConfirmModal.tsx'

export default function BasketEditOrderItemPage() {
  const { driveId, orderItemId } = useParams<{ driveId: string; orderItemId: string }>()
  const navigate = useNavigate()
  const { getBasketItemDetail } = useClient()

  const [basketItem, setBasketItem] = useState<BasketItem | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchOrderItem = async () => {
      if (!orderItemId) return

      try {
        setIsLoading(true)
        const response = await getBasketItemDetail(orderItemId)
        setBasketItem(response.data)
      } catch (error) {
        console.error('Error fetching order item:', error)
        setError('Fehler beim Laden des Warenkorb-Artikels')
      } finally {
        setIsLoading(false)
      }
    }

    fetchOrderItem()
  }, [orderItemId, getBasketItemDetail])

  const handleSelectPhotos = () => {
    navigate(`/d/${driveId}/shop/basket/edit/${orderItemId}/select-photos`)
  }

  const handleCropPhotos = () => {
    navigate(`/d/${driveId}/shop/basket/edit/${orderItemId}/crop-photos`)
  }

  const goBackToBasket = () => {
    navigate(`/d/${driveId}/shop/basket`)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading color="primary" />
      </div>
    )
  }

  if (error || !basketItem) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <h1 className="text-2xl font-bold text-error">
            {error || 'Warenkorb-Artikel nicht gefunden'}
          </h1>
          <Button color="primary" variant="outline" onClick={goBackToBasket}>
            <IconArrowBack size={20} />
            Zurück zum Warenkorb
          </Button>
        </div>
      </div>
    )
  }

  if (basketItem.product.sku == 'photobook') {
    return <PhotobookPageContent basketItem={basketItem} />
  }

  const formatPrice = (price: string) => {
    return `€${parseFloat(price).toFixed(2)}`
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <Button
          color="primary"
          variant="outline"
          size="sm"
          onClick={goBackToBasket}
        >
          <IconArrowBack size={20} />
          Zurück zum Warenkorb
        </Button>

        <div className="text-center">
          <h1 className="text-xl font-bold">Artikel bearbeiten</h1>
        </div>

        <div className="w-24" /> {/* Spacer for centering */}
      </div>

      {/* Basket item details */}
      <div className="bg-base-200 p-6 rounded-lg mb-6">
        <h2 className="text-2xl font-bold mb-2">{basketItem.product.title}</h2>
        {basketItem.variation && (
          <h3 className="text-lg font-semibold text-primary mb-2">
            {basketItem.variation.title_suffix}
          </h3>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <p className="text-sm opacity-60">Menge: {basketItem.quantity}</p>
            <p className="text-lg font-semibold">
              Preis: {formatPrice(basketItem.total_price)}
            </p>
          </div>

          {basketItem.playlist && (
            <div>
              <p className="text-sm opacity-60">
                Bilder: {basketItem.playlist.files?.length || 0}
              </p>
              <p className="text-sm opacity-60">
                Playlist: {basketItem.playlist.id}
              </p>
            </div>
          )}
        </div>

        {basketItem.product.description && (
          <p className="text-sm mt-4 opacity-75">
            {basketItem.product.description}
          </p>
        )}
      </div>

      {/* Current playlist preview */}
      {basketItem.playlist && basketItem.playlist.files && (
        <div className="bg-base-100 p-6 rounded-lg mb-6">
          <h3 className="text-lg font-semibold mb-4">Aktuelle Bildauswahl</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {basketItem.playlist.files.slice(0, 12).map((connection) => (
              <div key={connection.id} className="relative">
                { connection.file &&
                  <img
                    src={connection.file.thumbnail || connection.file.file}
                    alt="Selected image"
                    className="w-full h-24 object-cover rounded-lg"
                  />
                }
                {connection.count > 1 && (
                  <div className="absolute top-1 right-1 bg-primary text-primary-content text-xs px-2 py-1 rounded-full">
                    {connection.count}×
                  </div>
                )}
              </div>
            ))}
            {basketItem.playlist.files.length > 12 && (
              <div className="flex items-center justify-center h-24 bg-base-200 rounded-lg">
                <span className="text-sm opacity-60">
                  +{basketItem.playlist.files.length - 12} weitere
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Button
          color="primary"
          size="lg"
          className="w-full"
          onClick={handleSelectPhotos}
        >
          <IconEdit size={20} />
          Bildauswahl ändern
        </Button>

        <Button
          color="primary"
          variant="outline"
          size="lg"
          className="w-full"
          onClick={handleCropPhotos}
          disabled={!basketItem.playlist?.files?.length}
        >
          <IconEdit size={20} />
          Zuschnitt bearbeiten
        </Button>
      </div>
    </div>
  )
}

function PhotobookPageContent({ basketItem }: { basketItem: BasketItem }) {
  const [book, setBook] = useState(basketItem.playlist!)
  const { updateBasketItemDetails } = useClient()
  const [isSaving, setIsSaving] = useState(false)
  const { driveId, orderItemId } = useParams<{ driveId: string; orderItemId: string }>()
  const navigate = useNavigate()
  const { modalRef, show: showConfirmModal, close: closeConfirmModal } = useCropConfirmModal()

  const handleConfirmSave = async () => {
    if (!basketItem) return

    closeConfirmModal()
    setIsSaving(true)

    try {
      await updateBasketItemDetails(orderItemId!, {
        page_datas: book.pages,
        connection_datas: book.files.map(file => ({...file, file: file.file?.id || null})),
        update_existing: true
      })

      navigate(`/d/${driveId}/shop/basket`)
    } catch (error) {
      console.error('Error updating basket item:', error)
      alert('Fehler beim Speichern der Änderungen')
    } finally {
      setIsSaving(false)
    }
  }

  return <div className="container mx-auto px-4 py-8">
    <CropConfirmModal
      modalRef={modalRef}
      onConfirm={handleConfirmSave}
      isLoading={isSaving}
    />

    <div className='fixed bottom-0 left-0 w-full z-50 bg-secondary border-t-4 border-primary shadow-lg'>
      <div className='container px-4 mx-auto py-3 flex items-center justify-between flex-row h-[4rem]'>
        <Button className='ms-auto' size='sm' color='primary' onClick={showConfirmModal} loading={isSaving} disabled={isSaving}>
          Speichern
        </Button>
      </div>
    </div>
    <BookDesignEditor book={book} setBook={setBook} />
  </div>
}
